import React, { useState, useEffect, useRef, useMemo } from 'react';
import { createPortal } from 'react-dom';
import CircularProgress from '../../common/CircularProgress';

type Props = {
  open: boolean;
  onClose: () => void;
  scheduleId: string | number;
  weekdayIdx: number;
  dateLabel?: string;
  startTime: string;
  endTime: string;
  loading?: boolean;
  existingSchedules?: { startTime: string; endTime: string; id: string | number }[];
  scheduleType?: 'standard' | 'reschedule_only';
  onDelete: (scheduleId: string | number) => Promise<void> | void;
  onSave: (input: { scheduleId: string | number; weekdayIdx: number; startTime: string; endTime: string; scheduleType: 'standard' | 'reschedule_only' }) => Promise<void> | void;
};

function timeToMinutes(hhmm: string): number {
  const [hh, mm] = hhmm.split(':').map(Number);
  return hh * 60 + mm;
}

function hasOverlap(start1: string, end1: string, start2: string, end2: string): boolean {
  const s1 = timeToMinutes(start1);
  let e1 = timeToMinutes(end1);
  const s2 = timeToMinutes(start2);
  let e2 = timeToMinutes(end2);

  if (e1 === 0) e1 = 24 * 60;
  if (e2 === 0) e2 = 24 * 60;

  return s1 < e2 && e1 > s2;
}

const TIMES_30: string[] = Array.from({ length: 48 }).map((_, i) => {
  const hh = String(Math.floor(i / 2)).padStart(2, '0');
  const mm = i % 2 === 0 ? '00' : '30';
  return `${hh}:${mm}`;
});

const START_TIMES_30: string[] = TIMES_30.filter(t => {
  const m = parseInt(t.split(':')[0], 10) * 60 + parseInt(t.split(':')[1], 10);
  return (24 * 60 - m) >= 60;
});

// Find the closest option to a partial typed string (e.g. "1" -> "10:00", "14" -> "14:00")
function findClosestOption(typed: string, options: string[]): string | null {
  if (!typed) return null;
  const prefix = options.find(o => o.startsWith(typed));
  if (prefix) return prefix;
  const num = parseInt(typed.replace(':', ''), 10);
  if (isNaN(num)) return null;
  const digits = typed.replace(':', '');
  if (digits.length <= 2) {
    const hour = Math.min(23, Math.max(0, num));
    const target = `${String(hour).padStart(2, '0')}:00`;
    const match = options.find(o => o >= target);
    return match || options[options.length - 1] || null;
  }
  const h = Math.floor(num / 100);
  const m = num % 100;
  const target = `${String(Math.min(23, h)).padStart(2, '0')}:${String(Math.min(59, m)).padStart(2, '0')}`;
  const match = options.find(o => o >= target);
  return match || options[options.length - 1] || null;
}

type TimeSelectProps = {
  value: string;
  onChange: (v: string) => void;
  ariaLabel?: string;
  options?: string[];
};

const TimeSelect: React.FC<TimeSelectProps> = ({ value, onChange, ariaLabel, options }) => {
  const items = options || TIMES_30;
  const [open, setOpen] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [highlightedOption, setHighlightedOption] = useState<string | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const listRef = useRef<HTMLDivElement | null>(null);
  const [menuPos, setMenuPos] = useState<{ top: number; left: number; width: number; maxH: number }>({ top: 0, left: 0, width: 0, maxH: 260 });

  useEffect(() => {
    const onDocClick = (e: MouseEvent) => {
      if (!containerRef.current?.contains(e.target as Node) && !listRef.current?.contains(e.target as Node)) {
        setOpen(false);
        setIsEditing(false);
        setInputValue('');
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setOpen(false);
        setIsEditing(false);
        setInputValue('');
      }
    };
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);

    const updatePos = () => {
      const el = inputRef.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      const spaceBelow = window.innerHeight - r.bottom - 12;
      const maxH = Math.max(160, Math.min(320, spaceBelow));
      setMenuPos({ top: r.bottom + 4, left: r.left, width: Math.max(r.width, 100), maxH });
    };
    updatePos();
    if (open) {
      window.addEventListener('resize', updatePos);
      window.addEventListener('scroll', updatePos, true);
    }
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('resize', updatePos);
      window.removeEventListener('scroll', updatePos, true);
    };
  }, [open]);

  // Scroll to highlighted option
  useEffect(() => {
    if (!highlightedOption || !listRef.current) return;
    const el = listRef.current.querySelector(`[data-value="${highlightedOption}"]`) as HTMLElement | null;
    if (el) el.scrollIntoView({ block: 'nearest' });
  }, [highlightedOption]);

  // Scroll to current value when opening
  useEffect(() => {
    if (!open || !listRef.current || !value) return;
    const target = highlightedOption || value;
    const el = listRef.current.querySelector(`[data-value="${target}"]`) as HTMLElement | null;
    if (el) el.scrollIntoView({ block: 'nearest' });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handleInputChange = (typed: string) => {
    const clean = typed.replace(/[^\d:]/g, '');
    setInputValue(clean);
    const closest = findClosestOption(clean, items);
    setHighlightedOption(closest);
  };

  const handleInputKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      if (highlightedOption) {
        onChange(highlightedOption);
      }
      setOpen(false);
      setIsEditing(false);
      setInputValue('');
      e.preventDefault();
    }
  };

  const handleClick = () => {
    if (!open) {
      setOpen(true);
      setIsEditing(true);
      setInputValue('');
      setHighlightedOption(null);
      setTimeout(() => inputRef.current?.focus(), 0);
    }
  };

  return (
    <div ref={containerRef} style={{ position: 'relative' }}>
      <input
        ref={inputRef}
        type="text"
        aria-label={ariaLabel}
        value={isEditing ? inputValue : value}
        placeholder={value || '--:--'}
        onClick={handleClick}
        onFocus={() => {
          if (!open) {
            setOpen(true);
            setIsEditing(true);
            setInputValue('');
            setHighlightedOption(null);
          }
        }}
        onChange={(e) => handleInputChange(e.target.value)}
        onKeyDown={handleInputKeyDown}
        readOnly={!isEditing}
        style={{
          width: '100%',
          padding: '0.625rem 0.75rem',
          fontSize: '0.875rem',
          border: '1px solid #d1d5db',
          borderRadius: '8px',
          background: '#fff',
          textAlign: 'left',
          cursor: 'pointer',
          outline: 'none',
          borderColor: open ? '#6D57BC' : '#d1d5db',
        }}
      />
      {open && createPortal(
        <div
          ref={listRef}
          role="listbox"
          style={{
            position: 'fixed',
            top: `${menuPos.top}px`,
            left: `${menuPos.left}px`,
            width: `${menuPos.width}px`,
            background: '#fff',
            border: '1px solid #e5e7eb',
            borderRadius: 8,
            boxShadow: '0 10px 25px rgba(0,0,0,0.15)',
            maxHeight: menuPos.maxH,
            overflowY: 'auto',
            zIndex: 3000
          }}
        >
          {items.map(t => (
            <div
              key={t}
              data-value={t}
              role="option"
              aria-selected={t === value}
              onMouseDown={(e) => { e.preventDefault(); onChange(t); setOpen(false); setIsEditing(false); setInputValue(''); }}
              style={{
                padding: '0.5rem 0.75rem',
                cursor: 'pointer',
                background: t === highlightedOption ? '#ede9fe' : t === value ? '#f3f4f6' : '#fff',
                fontWeight: t === highlightedOption || t === value ? 600 : 400,
              }}
              onMouseEnter={(e) => { (e.currentTarget as HTMLDivElement).style.background = '#f5f3ff'; }}
              onMouseLeave={(e) => { (e.currentTarget as HTMLDivElement).style.background = t === highlightedOption ? '#ede9fe' : t === value ? '#f3f4f6' : '#fff'; }}
            >
              {t}
            </div>
          ))}
        </div>,
        document.body
      )}
    </div>
  );
};

export default function EditWorkRangeModal(props: Props) {
  const { open, onClose, scheduleId, weekdayIdx, dateLabel, startTime, endTime, loading, existingSchedules, scheduleType = 'standard', onDelete, onSave } = props;
  const [localStart, setLocalStart] = useState(startTime);
  const [localEnd, setLocalEnd] = useState(endTime);
  const [localScheduleType, setLocalScheduleType] = useState<'standard' | 'reschedule_only'>(scheduleType);
  const [error, setError] = useState<string>('');
  const [confirmDelete, setConfirmDelete] = useState(false);

  useEffect(() => {
    setLocalStart(startTime);
    setLocalEnd(endTime);
    setLocalScheduleType(scheduleType);
    setError('');
    setConfirmDelete(false);
  }, [startTime, endTime, scheduleType]);

  useEffect(() => {
    if (!localStart || !localEnd) {
      setError('');
      return;
    }

    const startMin = timeToMinutes(localStart);
    let endMin = timeToMinutes(localEnd);

    if (endMin === 0) {
      endMin = 24 * 60;
    }

    if (endMin <= startMin) {
      setError('La hora de fin debe ser posterior a la hora de inicio');
      return;
    }

    const otherSchedules = (existingSchedules || []).filter(s => s.id !== scheduleId);
    const overlapping = otherSchedules.find(s => hasOverlap(localStart, localEnd, s.startTime, s.endTime));

    if (overlapping) {
      setError(`Este rango se solapa con otro horario: ${overlapping.startTime} - ${overlapping.endTime}`);
      return;
    }

    setError('');
  }, [localStart, localEnd, scheduleId, existingSchedules]);

  // Compute available start options dynamically based on existing schedules
  const startOptions = useMemo(() => {
    const otherSchedules = (existingSchedules || [])
      .filter(s => s.id !== scheduleId)
      .map(s => ({
        start: timeToMinutes(s.startTime),
        end: s.endTime === '00:00' ? 24 * 60 : timeToMinutes(s.endTime),
      }))
      .sort((a, b) => a.start - b.start);

    const endMin = localEnd ? (localEnd === '00:00' ? 24 * 60 : timeToMinutes(localEnd)) : null;

    return START_TIMES_30.filter(t => {
      const candidateMin = timeToMinutes(t);

      // Enforce 60 min minimum: if end is selected, start must be at least 60 min before
      if (endMin !== null && candidateMin > endMin - 60) return false;

      // Check that at least 60 free minutes exist after this candidate
      const upperBound = candidateMin + 60;
      const blocked = otherSchedules.some(s => s.start < upperBound && s.end > candidateMin);
      return !blocked;
    });
  }, [existingSchedules, scheduleId, localEnd]);

  const endOptions = useMemo(() => {
    if (!localStart) return TIMES_30.filter(t => t !== '00:00');
    const startMin = timeToMinutes(localStart);
    const minEndMin = startMin + 60;
    const otherSchedules = (existingSchedules || [])
      .filter(s => s.id !== scheduleId)
      .map(s => ({
        start: timeToMinutes(s.startTime),
        end: s.endTime === '00:00' ? 24 * 60 : timeToMinutes(s.endTime),
      }));
    let maxEndMin = 24 * 60;
    for (const s of otherSchedules) {
      if (s.start > startMin && s.start < maxEndMin) {
        maxEndMin = s.start;
      }
    }
    return TIMES_30.filter(t => {
      if (t === '00:00') return false;
      const m = timeToMinutes(t);
      return m >= minEndMin && m <= maxEndMin;
    });
  }, [localStart, existingSchedules, scheduleId]);

  if (!open) return null;

  const canSave = localStart && localEnd && !error && !loading;

  return (
    <>
      <div className="modal-overlay" style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', direction: 'ltr', zIndex: 1000 }} onClick={onClose}>
        <div className="modal-content" style={{ background: '#fff', borderRadius: '12px', width: '32rem', maxWidth: '90vw', boxShadow: '0 20px 60px rgba(0,0,0,0.3)' }} onClick={(e) => e.stopPropagation()}>
          {loading ? (
            <div style={{ padding: '3rem', display: 'grid', placeItems: 'center' }}>
              <CircularProgress sizeRem={2.5} label="Guardando" />
            </div>
          ) : (
            <>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '1.5rem 1.5rem 1rem 1.5rem', borderBottom: '1px solid #e5e7eb' }}>
                <h3 style={{ margin: 0, fontSize: '1.25rem !important', fontWeight: 700, textAlign: 'left', color: '#111827' }}>Editar rango de trabajo</h3>
                <button style={{ background: 'transparent', border: 0, fontSize: '1.5rem !important', cursor: 'pointer', color: '#6b7280', padding: 0, width: '2rem', height: '2rem', display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '6px', transition: 'background 0.2s' }} onClick={onClose} onMouseEnter={(e) => e.currentTarget.style.background = '#f3f4f6'} onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}>×</button>
              </div>
              <div style={{ padding: '1.5rem', fontSize: '0.875rem !important', textAlign: 'left' }}>
                {dateLabel && (
                  <div style={{ marginBottom: '1.5rem', padding: '0.75rem', background: '#f9fafb', borderRadius: '8px', border: '1px solid #e5e7eb' }}>
                    <span style={{ fontSize: '0.875rem !important', color: '#6b7280', fontWeight: 700 }}>{dateLabel}</span>
                  </div>
                )}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
                  <div>
                    <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem !important', fontWeight: 700, color: '#374151' }}>Hora de inicio</label>
                    <TimeSelect
                      value={localStart}
                      onChange={setLocalStart}
                      ariaLabel="Hora de inicio"
                      options={startOptions}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem !important', fontWeight: 700, color: '#374151' }}>Hora de fin</label>
                    <TimeSelect
                      value={localEnd}
                      onChange={setLocalEnd}
                      ariaLabel="Hora de fin"
                      options={endOptions}
                    />
                  </div>
                </div>
                <div style={{ marginTop: '0.75rem' }}>
                  <label className="block mb-2 text-sm font-semibold text-gray-700">Tipo de horario</label>
                  <select
                    value={localScheduleType}
                    onChange={(e) => setLocalScheduleType(e.target.value as 'standard' | 'reschedule_only')}
                    className={`w-full py-2.5 px-3 text-sm border border-gray-300 rounded-lg bg-white outline-none cursor-pointer focus:border-violet-700 ${
                      localScheduleType === 'reschedule_only' ? 'text-amber-700' : 'text-gray-700'
                    }`}
                  >
                    <option value="standard">Estándar</option>
                    <option value="reschedule_only">Reprogramación</option>
                  </select>
                </div>
                {error && (
                  <div style={{ padding: '0.75rem', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '8px', color: '#dc2626', fontSize: '0.875rem !important', marginTop: '1rem' }}>
                    {error}
                  </div>
                )}
              </div>
              <div style={{ padding: '1rem 1.5rem 1.5rem 1.5rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderTop: '1px solid #e5e7eb' }}>
                <button
                  style={{
                    padding: '0.625rem 1rem',
                    fontSize: '0.875rem !important',
                    cursor: 'pointer',
                    background: '#fff',
                    color: '#dc2626',
                    border: '1px solid #dc2626',
                    borderRadius: '8px',
                    fontWeight: 700,
                    transition: 'all 0.2s'
                  }}
                  onClick={() => setConfirmDelete(true)}
                  onMouseEnter={(e) => { e.currentTarget.style.background = '#dc2626'; e.currentTarget.style.color = '#fff'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = '#fff'; e.currentTarget.style.color = '#dc2626'; }}
                >
                  Eliminar
                </button>
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  <button
                    style={{
                      padding: '0.625rem 1rem',
                      fontSize: '0.875rem !important',
                      cursor: 'pointer',
                      background: '#fff',
                      color: '#6b7280',
                      border: '1px solid #d1d5db',
                      borderRadius: '8px',
                      fontWeight: 700,
                      transition: 'all 0.2s'
                    }}
                    onClick={onClose}
                    onMouseEnter={(e) => e.currentTarget.style.background = '#f9fafb'}
                    onMouseLeave={(e) => e.currentTarget.style.background = '#fff'}
                  >
                    Cancelar
                  </button>
                  <button
                    disabled={!canSave}
                    style={{
                      padding: '0.625rem 1.25rem',
                      fontSize: '0.875rem !important',
                      cursor: canSave ? 'pointer' : 'not-allowed',
                      background: canSave ? '#6D57BC' : '#9ca3af',
                      color: '#fff',
                      border: 0,
                      borderRadius: '8px',
                      fontWeight: 700,
                      transition: 'all 0.2s',
                      opacity: canSave ? 1 : 0.6
                    }}
                    onClick={async () => {
                      if (canSave) {
                        await onSave({ scheduleId, weekdayIdx, startTime: localStart, endTime: localEnd, scheduleType: localScheduleType });
                        onClose();
                      }
                    }}
                    onMouseEnter={(e) => { if (canSave) e.currentTarget.style.background = '#5b4a9e'; }}
                    onMouseLeave={(e) => { if (canSave) e.currentTarget.style.background = '#6D57BC'; }}
                  >
                    Guardar
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {confirmDelete && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1100 }} onClick={() => setConfirmDelete(false)}>
          <div style={{ background: '#fff', borderRadius: '12px', width: '26rem', maxWidth: '90vw', boxShadow: '0 20px 60px rgba(0,0,0,0.3)', padding: '1.5rem' }} onClick={(e) => e.stopPropagation()}>
            <h4 style={{ margin: '0 0 0.75rem 0', fontSize: '1rem !important', fontWeight: 700, color: '#111827', textAlign: 'left' }}>Eliminar rango de trabajo</h4>
            <p style={{ margin: '0 0 1.25rem 0', fontSize: '0.875rem !important', color: '#6b7280', lineHeight: 1.5, textAlign: 'left' }}>
              Esta acción no afecta tus sesiones agendadas, pero si puede afectar tu disponibilidad para futuros agendamientos y/o pacientes.
            </p>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem' }}>
              <button
                style={{
                  padding: '0.625rem 1rem',
                  fontSize: '0.875rem !important',
                  cursor: 'pointer',
                  background: '#fff',
                  color: '#6b7280',
                  border: '1px solid #d1d5db',
                  borderRadius: '8px',
                  fontWeight: 700,
                  transition: 'all 0.2s'
                }}
                onClick={() => setConfirmDelete(false)}
                onMouseEnter={(e) => e.currentTarget.style.background = '#f9fafb'}
                onMouseLeave={(e) => e.currentTarget.style.background = '#fff'}
              >
                Cancelar
              </button>
              <button
                style={{
                  padding: '0.625rem 1rem',
                  fontSize: '0.875rem !important',
                  cursor: 'pointer',
                  background: '#dc2626',
                  color: '#fff',
                  border: 0,
                  borderRadius: '8px',
                  fontWeight: 700,
                  transition: 'all 0.2s'
                }}
                onClick={async () => { await onDelete(scheduleId); setConfirmDelete(false); onClose(); }}
                onMouseEnter={(e) => e.currentTarget.style.background = '#b91c1c'}
                onMouseLeave={(e) => e.currentTarget.style.background = '#dc2626'}
              >
                Confirmar
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
